import React from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const knowledgeCalls = [
  {
    title: "Add Real-Time MWD Data Interpretation Techniques",
    relatedTo: "Kick Detection and Well Control Events"
  },
  {
    title: "Share Success Criteria for EOR Polymer Injection Campaigns",
    relatedTo: "Enhanced Oil Recovery Pilot Learnings"
  },
  {
    title: "Submit Bit Wear Analysis Workflows for Shale Formations",
    relatedTo: "Horizontal Wellbore Cleaning Strategies"
  },
  {
    title: "Field-Validated NPT Logs for Learning-Driven Categorization",
    relatedTo: "Reducing Non-Productive Time (NPT)"
  },
  {
    title: "Include Remote Sensing Inputs into Seismic Interpretation Models",
    relatedTo: "Seismic-to-Reservoir Workflow Best Practices"
  },
  {
    title: "Submit HPHT Incident Reports for Risk Modeling Patterns",
    relatedTo: "Safety Protocol KB for HPHT Drilling"
  },
  {
    title: "Add Field Training Modules on Fracture Fluid Additives",
    relatedTo: "Fracture Stimulation Design Optimization"
  },
  {
    title: "Share Re-Entry Planning Guides from Decommissioned Fields",
    relatedTo: "Aging Well Re-Entry and Recompletion"
  },
  {
    title: "Contribute Wellsite Troubleshooting Procedures from Rig Crews",
    relatedTo: "Drilling Performance Metrics"
  },
  {
    title: "Submit AI-Ready Labeling Formats for Well Logs and Cuttings Data",
    relatedTo: "AI Framework for Drilling Metrics"
  }
];

const ChevronCallForKnowledge = () => {
  const navigate = useNavigate();

  const handleCallClick = (callTitle: string, relatedTo: string) => {
    // Navigate to the CfK flow with pre-populated Chevron-specific data
    navigate("/call-for-knowledge/bounty", {
      state: {
        selectedArea: "Chevron Energy Operations",
        chevronCall: {
          title: callTitle,
          relatedTo: relatedTo,
          organization: "Chevron",
          bountyAmount: "2000", // CfK gets $2k
          bountyType: "Enterprise",
          contributors: ["Trey Fleming", "Karen Watson", "Pete Dumont", "Giblet"],
          description: `Chevron seeks knowledge contributions for: ${callTitle}. This knowledge request aims to capture field expertise and operational insights to enhance safety, efficiency, and best practices across our global operations.`
        }
      }
    });
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to="/chevron-channel">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Chevron Channel
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Oil and Gas Demonstration
          </h1>
          <p className="text-lg text-muted-foreground max-w-4xl mb-6">
            These are invitations to contribute new knowledge — typically from field experiences, 
            legacy systems, or underrepresented workflows.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <BookOpen className="h-6 w-6" />
              Calls for Knowledge (CfK)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {knowledgeCalls.map((call, index) => (
                <Card key={index} className="border border-muted hover:border-primary/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-3">
                      <button 
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer text-left"
                        onClick={() => handleCallClick(call.title, call.relatedTo)}
                      >
                        CfK: {call.title}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChevronCallForKnowledge;